import React from 'react';
import PropTypes from 'prop-types';
import { overridesShape } from './shapes';
import { TimezoneFallbackBadge } from './Summary';
import {
  ETHNICITY_OPTIONS,
  GENDER_PREF_OPTIONS,
  STUDENT_GENDER_OPTIONS,
  goalName,
  slotAlignError
} from './constants';

function SelectField({ id, label, value, options, onChange, emptyLabel }) {
  return (
    <label className="rv2-field" htmlFor={id}>
      {label}
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{emptyLabel}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

SelectField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string }))
    .isRequired,
  onChange: PropTypes.func.isRequired,
  emptyLabel: PropTypes.string
};

SelectField.defaultProps = {
  emptyLabel: "Use student's data"
};

const metaGoalsPropType = PropTypes.arrayOf(
  PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({ display_name: PropTypes.string, goal_display_name: PropTypes.string })
  ])
);

const toOptions = (list) => list.map((v) => ({ value: v, label: v }));
const BOOL_OPTIONS = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' }
];

function OverridesPanel({ overrides, setOverride, metaGoals, studentGoals }) {
  const goalNames = [...new Set([...metaGoals.map(goalName), ...studentGoals].filter(Boolean))];
  const overridingGoals = Array.isArray(overrides.goals);

  const toggleGoal = (name) => {
    const current = overrides.goals || [];
    setOverride(
      'goals',
      current.includes(name) ? current.filter((g) => g !== name) : [...current, name]
    );
  };

  return (
    <div className="rv2-card">
      <h3>Overrides</h3>
      <div className="rv2-hint" style={{ marginBottom: 8 }}>
        Leave a field on &quot;Use student&apos;s data&quot; to let the server resolve it.
      </div>
      <div className="rv2-grid">
        <SelectField
          id="rv2-gender-pref"
          label="Teacher gender preference"
          value={overrides.teacher_gender_preference}
          options={toOptions(GENDER_PREF_OPTIONS)}
          onChange={(v) => setOverride('teacher_gender_preference', v)}
        />
        <SelectField
          id="rv2-student-gender"
          label="Student gender"
          value={overrides.student_gender}
          options={toOptions(STUDENT_GENDER_OPTIONS)}
          onChange={(v) => setOverride('student_gender', v)}
        />
        <SelectField
          id="rv2-ethnicity"
          label="Student ethnicity"
          value={overrides.student_ethnicity}
          options={toOptions(ETHNICITY_OPTIONS)}
          onChange={(v) => setOverride('student_ethnicity', v)}
        />
        <label className="rv2-field" htmlFor="rv2-country">
          Country ISO
          <input
            id="rv2-country"
            value={overrides.country_iso}
            maxLength={2}
            placeholder="e.g. US"
            onChange={(e) => setOverride('country_iso', e.target.value)}
          />
        </label>
        <SelectField
          id="rv2-referral"
          label="Referral student"
          value={overrides.is_referral_student}
          options={BOOL_OPTIONS}
          onChange={(v) => setOverride('is_referral_student', v)}
        />
        <SelectField
          id="rv2-prenatal"
          label="Prenatal student"
          value={overrides.is_prenatal_student}
          options={BOOL_OPTIONS}
          onChange={(v) => setOverride('is_prenatal_student', v)}
        />
        <label className="rv2-check" htmlFor="rv2-disable-relax">
          <input
            id="rv2-disable-relax"
            type="checkbox"
            checked={overrides.disable_thin_pool_relaxation}
            onChange={(e) => setOverride('disable_thin_pool_relaxation', e.target.checked)}
          />
          Disable thin-pool relaxation
        </label>
      </div>
      <div style={{ marginTop: 12 }}>
        <label className="rv2-check" htmlFor="rv2-override-goals">
          <input
            id="rv2-override-goals"
            type="checkbox"
            checked={overridingGoals}
            onChange={(e) => setOverride('goals', e.target.checked ? [...studentGoals] : null)}
          />
          Override goals
        </label>
        {overridingGoals ? (
          <div className="rv2-goal-list">
            {goalNames.length === 0 ? (
              <span className="rv2-empty">Goal vocabulary not loaded (meta API).</span>
            ) : null}
            {goalNames.map((name) => (
              <label key={name} className="rv2-check" htmlFor={`rv2-goal-${name}`}>
                <input
                  id={`rv2-goal-${name}`}
                  type="checkbox"
                  checked={overrides.goals.includes(name)}
                  onChange={() => toggleGoal(name)}
                />
                {name}
              </label>
            ))}
            {overrides.goals.length === 0 ? (
              <span className="rv2-hint">No goals selected: goal score will be 0.</span>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

OverridesPanel.propTypes = {
  overrides: overridesShape.isRequired,
  setOverride: PropTypes.func.isRequired,
  metaGoals: metaGoalsPropType.isRequired,
  studentGoals: PropTypes.arrayOf(PropTypes.string).isRequired
};

function InputsPanel({
  email,
  setEmail,
  phaseAOnly,
  setPhaseAOnly,
  slotDate,
  setSlotDate,
  slotTime,
  setSlotTime,
  timezone,
  timezoneIsFallback,
  slotLabel,
  topN,
  setTopN,
  writeAudit,
  setWriteAudit,
  overrides,
  setOverride,
  metaGoals,
  studentGoals
}) {
  const timeError = phaseAOnly || !timezone ? '' : slotAlignError(slotDate, slotTime, timezone);
  let tzHint = 'Student timezone loads with the student; slot defaults to tomorrow 07:00.';
  if (timezone) tzHint = `Student timezone: ${timezone}`;
  return (
    <>
      <div className="rv2-card">
        <h3>Inputs</h3>
        <div className="rv2-grid">
          <label className="rv2-field" htmlFor="rv2-email">
            Student email
            <input
              id="rv2-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          {phaseAOnly ? null : (
            <>
              <label className="rv2-field" htmlFor="rv2-date">
                Slot date (student local)
                <input
                  id="rv2-date"
                  type="date"
                  value={slotDate}
                  onChange={(e) => setSlotDate(e.target.value)}
                />
              </label>
              <label className="rv2-field" htmlFor="rv2-time">
                Slot time (student local)
                <input
                  id="rv2-time"
                  type="time"
                  step={1800}
                  value={slotTime}
                  onChange={(e) => setSlotTime(e.target.value)}
                />
                {timeError ? <span className="rv2-field-error">{timeError}</span> : null}
              </label>
            </>
          )}
          <label className="rv2-field" htmlFor="rv2-topn">
            Top N
            <input
              id="rv2-topn"
              type="number"
              min={1}
              max={50}
              value={topN}
              onChange={(e) => setTopN(e.target.value)}
            />
          </label>
          <label className="rv2-check" htmlFor="rv2-phase-a">
            <input
              id="rv2-phase-a"
              type="checkbox"
              checked={phaseAOnly}
              onChange={(e) => setPhaseAOnly(e.target.checked)}
            />
            Phase A only (no slot)
          </label>
          <label className="rv2-check" htmlFor="rv2-audit">
            <input
              id="rv2-audit"
              type="checkbox"
              checked={writeAudit}
              onChange={(e) => setWriteAudit(e.target.checked)}
            />
            Write audit row
          </label>
        </div>
        <div className="rv2-hint" style={{ marginTop: 8 }}>
          {tzHint} {timezone && timezoneIsFallback ? <TimezoneFallbackBadge /> : null}
          {slotLabel ? ` · Last resolved slot: ${slotLabel}` : ''}
        </div>
      </div>
      <OverridesPanel
        overrides={overrides}
        setOverride={setOverride}
        metaGoals={metaGoals}
        studentGoals={studentGoals}
      />
    </>
  );
}

InputsPanel.propTypes = {
  email: PropTypes.string.isRequired,
  setEmail: PropTypes.func.isRequired,
  phaseAOnly: PropTypes.bool.isRequired,
  setPhaseAOnly: PropTypes.func.isRequired,
  slotDate: PropTypes.string.isRequired,
  setSlotDate: PropTypes.func.isRequired,
  slotTime: PropTypes.string.isRequired,
  setSlotTime: PropTypes.func.isRequired,
  timezone: PropTypes.string.isRequired,
  timezoneIsFallback: PropTypes.bool.isRequired,
  slotLabel: PropTypes.string,
  topN: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  setTopN: PropTypes.func.isRequired,
  writeAudit: PropTypes.bool.isRequired,
  setWriteAudit: PropTypes.func.isRequired,
  overrides: overridesShape.isRequired,
  setOverride: PropTypes.func.isRequired,
  metaGoals: metaGoalsPropType,
  studentGoals: PropTypes.arrayOf(PropTypes.string)
};

InputsPanel.defaultProps = {
  slotLabel: '',
  metaGoals: [],
  studentGoals: []
};

export default InputsPanel;
